import { useTranslation } from 'react-i18next';
import { Link } from 'react-scroll';
import { Link as RouterLink } from 'react-router-dom';
import { logoAuxis } from '../../assets/images';
import { MapPin, Mail, Phone, ArrowUpRight, Facebook, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const scrollProps = {
    smooth: true,
    duration: 500,
    spy: true,
    offset: -80,
    className: "text-sm text-white/60 hover:text-auxis-gold transition-colors duration-300 cursor-pointer block font-medium tracking-wide"
  };

  const quickLinks = [
    { name: t('accueil'), to: "home" },
    { name: t('services'), to: "services" },
    { name: t('projets'), to: "projects" },
    { name: t('expertises'), to: "expertises" },
    { name: t('navbar.references', 'Références'), to: "references" },
    { name: t('contact'), to: "contact" }
  ];

  const contactItems = [
    { text: "Lot II A 28 PZ, Amboditsiry", icon: MapPin, href: "https://maps.google.com/?q=Lot+II+A+28+PZ+Amboditsiry" },
    { text: "services@auxis.mg", icon: Mail, href: "mailto:services@auxis.mg" },
    { text: "+261 34 08 866 86", icon: Phone, href: "tel:+261340886686" }
  ];

  return (
    <footer className="bg-auxis-charcoal pt-20 pb-10 text-white relative overflow-hidden font-quicksand border-t border-white/5 transition-colors duration-300">
      {/* Subtle grid pattern background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 pointer-events-none" />
      
      {/* Decorative gradient blob */}
      <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-auxis-gold/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container-wide relative z-10 px-6 lg:px-12 mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-16">
          
          {/* Brand Column */}
          <div className="lg:col-span-1 space-y-6">
            <a href="/" className="inline-block group transition-all duration-300">
              <img 
                src={logoAuxis} 
                alt="AUXIS" 
                className="h-10 w-auto opacity-100 transition-all duration-500 ease-in-out" 
              />
            </a>
            <p className="text-sm leading-relaxed text-white/60 max-w-xs font-light">
              {t('footer.description', 'Votre partenaire de confiance en ingénierie et construction à Madagascar. Alliance d\'expertise technique et d\'innovation pour bâtir l\'avenir.')}
            </p>
            <div className="flex items-center gap-3 pt-2">
                <RouterLink to="/maintenance" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-auxis-gold hover:border-auxis-gold hover:text-white transition-all duration-300 hover:-translate-y-1">
                    <Facebook size={18} />
                </RouterLink>
                <RouterLink to="/maintenance" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-auxis-gold hover:border-auxis-gold hover:text-white transition-all duration-300 hover:-translate-y-1">
                    <Linkedin size={18} />
                </RouterLink>
                 <RouterLink to="/maintenance" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-auxis-gold hover:border-auxis-gold hover:text-white transition-all duration-300 hover:-translate-y-1">
                    <Instagram size={18} />
                </RouterLink>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold text-white uppercase tracking-widest pl-1 border-l-2 border-auxis-gold/30">{t('footer.navigation', 'Navigation')}</h3>
            <ul className="space-y-3 pl-1">
              {quickLinks.map(link => (
                <li key={link.name}>
                  <Link to={link.to} {...scrollProps} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-auxis-gold hover:translate-x-1 transition-all duration-300 cursor-pointer group">
                    <span className="w-1 h-1 rounded-full bg-white/20 group-hover:bg-auxis-gold transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold text-white uppercase tracking-widest pl-1 border-l-2 border-auxis-gold/30">{t('footer.contact.title', 'Contact')}</h3>
            <ul className="space-y-4 pl-1">
              {contactItems.map((item, idx) => (
                <li key={idx}>
                  <a href={item.href || '#'} className="group flex items-start gap-3">
                    <div className="mt-0.5 text-white/40 group-hover:text-auxis-gold transition-colors duration-300 shrink-0">
                      <item.icon size={16} strokeWidth={1.5} />
                    </div>
                    <div>
                        <span className="block text-sm text-white/60 group-hover:text-white transition-colors duration-300 leading-tight">
                            {item.text}
                        </span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA & Newsletter */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold text-white uppercase tracking-widest pl-1 border-l-2 border-auxis-gold/30">{t('footer.newsletter.title', 'Restez Informé')}</h3>
            <p className="text-sm text-white/50 leading-relaxed mb-4 pl-1">
             {t('footer.newsletter.description', 'Inscrivez-vous à notre newsletter pour recevoir nos dernières actualités.')}
            </p>
            <form className="flex flex-col gap-3 pl-1" onSubmit={(e) => e.preventDefault()}>
              <div className="relative">
                <input 
                  type="email" 
                  placeholder={t('footer.newsletter.placeholder', 'Votre adresse email')}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-auxis-gold/50 focus:bg-white/10 transition-all duration-300"
                  required
                />
              </div>
              <button 
                type="submit"
                className="inline-flex items-center justify-center w-full gap-2 px-6 py-3 bg-auxis-gold text-white text-xs font-bold uppercase tracking-wider rounded-lg hover:bg-auxis-gold-light transition-all duration-300 group"
              >
                {t('footer.newsletter.button', 'S\'abonner')}
                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p className="text-white/40 text-xs font-light tracking-wide">
              &copy; {new Date().getFullYear()} AUXIS. {t('footer.rights')} | Powered by AUXIS
            </p>
            <div className="flex gap-6">
               <a href="/confidentialite" className="text-white/40 hover:text-auxis-gold transition-colors text-xs uppercase tracking-wider">{t('footer.legal.privacy')}</a>
               <a href="/mentions-legales" className="text-white/40 hover:text-auxis-gold transition-colors text-xs uppercase tracking-wider">{t('footer.legal.terms')}</a>
            </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
